import { useState } from 'react';
import { db } from '../lib/supabase';

// WaitlistPopup — shown after 5s to new visitors on the public homepage
// Replaces NewsletterPopup. Collects email for waitlist via newsletter_subscribers table.
// Shows T&C consent text inline under the CTA button.

export default function WaitlistPopup({ onClose, profile }) {
  const [email,   setEmail]   = useState(profile?.email || '');
  const [name,    setName]    = useState(profile?.full_name || '');
  const [done,    setDone]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  const isLoggedIn = !!profile?.email;

  const submit = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) { setErr('Please enter a valid email address.'); return; }
    setLoading(true); setErr('');
    try {
      const { error } = await db.subscribeNewsletter(trimmed, name.trim() || null);
      if (error && !error.message?.includes('row-level security') && error.code !== '42501') {
        throw error;
      }
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const FONT = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
  const PRIMARY = '#2B1DC9';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9500,
      background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, animation: 'fadeIn .3s ease',
      fontFamily: FONT,
    }}>
      <div style={{
        background: '#0E0C1A',
        border: '1px solid rgba(255,255,255,.1)',
        borderRadius: 20, padding: '36px 32px',
        width: '100%', maxWidth: 440,
        boxShadow: '0 32px 80px rgba(0,0,0,.7)',
        position: 'relative',
        animation: 'slideUp .3s cubic-bezier(.4,0,.2,1)',
      }}>
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 14, right: 14,
          background: 'rgba(255,255,255,.07)', border: 'none',
          color: '#94a3b8', borderRadius: 8, width: 30, height: 30,
          cursor: 'pointer', fontSize: 16, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>✕</button>

        <style>{`
          @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
          @keyframes slideUp { from { opacity:0; transform:translateY(24px) scale(.97) } to { opacity:1; transform:none } }
        `}</style>

        {done ? (
          /* ── Success state ── */
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
            <h3 style={{ margin: '0 0 10px', color: '#f1f5f9', fontSize: 22, fontWeight: 800 }}>
              You're on the list!
            </h3>
            <p style={{ margin: '0 0 24px', color: '#94a3b8', fontSize: 14, lineHeight: 1.7 }}>
              We'll reach out when early access opens. Keep an eye on your inbox.
            </p>
            <button onClick={onClose} style={{
              padding: '11px 28px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg,${PRIMARY},#7C3AED)`,
              color: '#fff', fontFamily: FONT, fontWeight: 700,
              fontSize: 14, cursor: 'pointer',
            }}>
              Got it →
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            {/* Icon */}
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: `linear-gradient(135deg,${PRIMARY},#7C3AED)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, margin: '0 auto 20px',
              boxShadow: '0 8px 24px rgba(43,29,201,.35)',
            }}>🚀</div>

            {/* Heading */}
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <h3 style={{ margin: '0 0 8px', color: '#f1f5f9', fontSize: 21, fontWeight: 800 }}>
                Aspreo is invite-only
              </h3>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: 14, lineHeight: 1.65 }}>
                We're in early access. Join the waitlist and be first to know when we open up — plus get exclusive early-bird benefits.
              </p>
            </div>

            {/* Fields — skip if already logged in (email auto-filled) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {!isLoggedIn && (
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  style={{
                    padding: '11px 14px', borderRadius: 10,
                    border: '1px solid rgba(255,255,255,.12)',
                    background: 'rgba(255,255,255,.06)',
                    color: '#f1f5f9', fontFamily: FONT, fontSize: 14, outline: 'none',
                  }}
                />
              )}
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()}
                placeholder="Your email address *"
                type="email"
                disabled={isLoggedIn}
                style={{
                  padding: '11px 14px', borderRadius: 10,
                  border: `1px solid ${err ? '#f43f5e44' : 'rgba(255,255,255,.12)'}`,
                  background: isLoggedIn ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.06)',
                  color: '#f1f5f9', fontFamily: FONT, fontSize: 14, outline: 'none',
                  opacity: isLoggedIn ? 0.7 : 1,
                }}
              />
            </div>

            {err && (
              <div style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>{err}</div>
            )}

            {/* CTA */}
            <button
              onClick={submit}
              disabled={loading}
              style={{
                width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                background: loading ? '#334155' : `linear-gradient(135deg,${PRIMARY},#7C3AED)`,
                color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(43,29,201,.4)',
                transition: 'all .2s', marginBottom: 12,
              }}
            >
              {loading ? '⏳ Joining...' : '✉️ Join the Waitlist'}
            </button>

            {/* T&C consent — small italic text */}
            <p style={{
              textAlign: 'center', fontSize: 11,
              color: 'rgba(255,255,255,.3)',
              lineHeight: 1.6, margin: 0,
              fontStyle: 'italic',
            }}>
              By clicking "Join the Waitlist" you agree to Aspreo's{' '}
              <a href="/terms" target="_blank" rel="noreferrer"
                style={{ color: 'rgba(255,255,255,.45)', textDecoration: 'underline' }}>
                Terms & Conditions
              </a>
              {' '}and confirm that you agree to Aspreo sending you product news, updates,
              and early-access announcements to the email above. You can unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
